import React, { useRef, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, Animated, Easing } from 'react-native';
import { BlurView } from 'expo-blur';
import LoadingSpinner from '../components/LoadingSpinner';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Path, G, ClipPath, Rect, Defs } from 'react-native-svg';
import { Input } from './RegisterScreen';
import useSignIn from '../hooks/useSignIn';
import GradientText from '../components/GradientText';
import { ScreenBackground } from '../components/ThemeGradients';

import { isMobile } from '../utils/responsive';
import { lookSheets, useLookStyles, useOriginalPolish, AUTH_FL_OVERRIDES } from './originalLook';

const FONT = Platform.OS === 'web'
  ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif"
  : 'System';

const webTransition = Platform.OS === 'web'
  ? { transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease, background-color 200ms ease, opacity 200ms ease' }
  : {};

const GoogleIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 21 20" fill="none">
    <Defs>
      <ClipPath id="clip0_384_103">
        <Rect width="20" height="20" fill="white" transform="translate(0.738525)" />
      </ClipPath>
    </Defs>
    <G clipPath="url(#clip0_384_103)">
      <Path d="M20.728 10.1871C20.728 9.36767 20.6599 8.76973 20.5127 8.14966H10.9377V11.848H16.558C16.4448 12.7671 15.8329 14.1512 14.4731 15.0813L14.454 15.2051L17.4814 17.4969L17.6912 17.5174C19.6175 15.7789 20.728 13.221 20.728 10.1871Z" fill="#1769F2" />
      <Path d="M10.9378 19.9313C13.6912 19.9313 16.0028 19.0454 17.6912 17.5174L14.4731 15.0813C13.612 15.6682 12.4561 16.0779 10.9378 16.0779C8.24095 16.0779 5.95204 14.3395 5.13611 11.9366L5.01652 11.9466L1.86856 14.3273L1.82739 14.4391C3.50441 17.6945 6.94913 19.9313 10.9378 19.9313Z" fill="#298642" />
      <Path d="M5.136 11.9367C4.92071 11.3166 4.79611 10.6522 4.79611 9.96571C4.79611 9.27915 4.92071 8.61479 5.12467 7.99472L5.11897 7.86266L1.93157 5.44373L1.82728 5.4922C1.1361 6.84311 0.739502 8.36014 0.739502 9.96571C0.739502 11.5713 1.1361 13.0882 1.82728 14.4391L5.136 11.9367Z" fill="#967002" />
      <Path d="M10.9378 3.85336C12.8527 3.85336 14.1445 4.66168 14.881 5.33718L17.7592 2.59107C15.9916 0.985496 13.6912 0 10.9378 0C6.94913 0 3.50441 2.23672 1.82739 5.49214L5.12479 7.99466C5.95205 5.59183 8.24095 3.85336 10.9378 3.85336Z" fill="#E92F20" />
    </G>
  </Svg>
);


export default function SignInScreen() {
  const styles = useLookStyles(SHEETS);
  // Floodlight polish; reduced motion skips the entrance and the exit fade (Classic keeps 600ms).
  const { isFloodlight, still, root } = useOriginalPolish();
  const {
    email, setEmail, password, setPassword,
    emailLoading, googleLoading, isBusy,
    status, statusType,
    pageOpacity, emailRef, passwordRef,
    handleSignIn, handleGoogleSignIn, handleForgotPassword, goToRegister,
  } = useSignIn({ exitDuration: still ? 0 : 600 });
  const fl = (props) => (isFloodlight ? props : {});

  // Entrance animation values
  const headerAnim = useRef(new Animated.Value(still ? 1 : 0)).current;
  const cardAnim = useRef(new Animated.Value(still ? 1 : 0)).current;

  // Page load animation: header rises in, card follows just behind it (skipped under reduced motion in Floodlight)
  useEffect(() => {
    if (still) return;
    Animated.parallel([
      Animated.timing(headerAnim, { toValue: 1, duration: 600, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      Animated.timing(cardAnim, { toValue: 1, duration: 650, delay: 120, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
    ]).start();
  }, []);

  const rise = (anim, from = 14) => ({
    opacity: anim,
    transform: [{ translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [from, 0] }) }],
  });

  const cardEntrance = {
    opacity: cardAnim,
    transform: [
      { translateY: cardAnim.interpolate({ inputRange: [0, 1], outputRange: [16, 0] }) },
      { scale: cardAnim.interpolate({ inputRange: [0, 1], outputRange: [0.98, 1] }) },
    ],
  };

  return (
    <View style={styles.container} {...root}>
      <ScreenBackground />
      <Animated.View style={[styles.mainContent, { opacity: pageOpacity }]}>
        <Animated.View style={[styles.headerSection, rise(headerAnim, -12)]}>
          <Text style={styles.brandTitle}>BETOPTIMIZED</Text>
          <Text style={styles.brandTagline}>EVERY BOOK · EVERY MARKET · ONE EDGE</Text>
        </Animated.View>

        <Animated.View style={[styles.formContainer, cardEntrance]}>
          <BlurView intensity={20} tint="dark" style={styles.formCard}>
            <View style={styles.formContent}>
              <View style={styles.titleSection}>
                <GradientText style={styles.welcomeTitle} gradientColors={['#FFFFFF', '#A9C2E8']}>Welcome back</GradientText>
                <Text style={styles.welcomeSubtitle}>Sign in to pick up right where you left off.</Text>
              </View>

              <View style={styles.formSection}>
                <Input
                  ref={emailRef}
                  label="Email or username"
                  placeholder="you@example.com"
                  disabled={isBusy}
                  value={email}
                  onChangeText={setEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  returnKeyType="next"
                  blurOnSubmit={false}
                  onSubmitEditing={() => passwordRef.current?.focus()}
                  autoComplete="username"
                />
                <Input
                  ref={passwordRef}
                  label="Password"
                  labelRight={
                    <Pressable onPress={handleForgotPassword} disabled={isBusy} hitSlop={8} {...fl({ accessibilityRole: 'link', accessibilityLabel: 'Forgot password?' })}>
                      {({ hovered }) => (
                        <Text style={[styles.forgotText, hovered && !isBusy && styles.linkTextHover, isBusy && styles.textDisabled]}>
                          Forgot password?
                        </Text>
                      )}
                    </Pressable>
                  }
                  placeholder="Enter your password"
                  disabled={isBusy}
                  value={password}
                  onChangeText={setPassword}
                  autoCapitalize="none"
                  secureTextEntry={true}
                  returnKeyType="go"
                  onSubmitEditing={handleSignIn}
                  autoComplete="current-password"
                />
                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.signInButton,
                    isBusy && styles.buttonDisabled,
                    hovered && !isBusy && styles.primaryHover,
                    pressed && !isBusy && styles.buttonPressed,
                  ]}
                  onPress={handleSignIn}
                  disabled={isBusy}
                  {...fl({ accessibilityRole: 'button', accessibilityLabel: emailLoading ? 'Signing you in' : 'Sign in' })}
                >
                  <LinearGradient
                    colors={['rgba(59, 130, 246, 0.32)', 'rgba(30, 80, 220, 0.16)']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.buttonGradient}
                  >
                    {emailLoading ? (
                      <View style={styles.loadingContainer}>
                        <LoadingSpinner size={16} color="rgba(255, 255, 255, 0.9)" />
                        <Text style={styles.signInButtonText}>Signing you in…</Text>
                      </View>
                    ) : (
                      <Text style={styles.signInButtonText}>Sign In</Text>
                    )}
                  </LinearGradient>
                </Pressable>
              </View>

              <View style={styles.altSection}>
                <View style={styles.dividerSection}>
                  <View style={styles.dividerLine} />
                  <Text style={styles.dividerText}>or</Text>
                  <View style={styles.dividerLine} />
                </View>

                <Pressable
                  style={({ pressed, hovered }) => [
                    styles.googleButton,
                    isBusy && styles.buttonDisabled,
                    hovered && !isBusy && styles.googleHover,
                    pressed && !isBusy && styles.buttonPressed,
                  ]}
                  onPress={handleGoogleSignIn}
                  disabled={isBusy}
                  {...fl({ accessibilityRole: 'button', accessibilityLabel: 'Continue with Google' })}
                >
                  <LinearGradient
                    colors={['rgba(0, 0, 0, 0.16)', 'rgba(116, 132, 154, 0.16)']}
                    style={styles.googleButtonGradient}
                  >
                    {googleLoading ? (
                      <View style={styles.loadingContainer}>
                        <LoadingSpinner size={16} color="rgba(255, 255, 255, 0.9)" />
                        <Text style={styles.googleButtonText}>Connecting to Google…</Text>
                      </View>
                    ) : (
                      <>
                        <GoogleIcon />
                        <Text style={styles.googleButtonText}>Continue with Google</Text>
                      </>
                    )}
                  </LinearGradient>
                </Pressable>
              </View>

              <View style={styles.footerSection}>
                {status ? (
                  <Text {...fl({ accessibilityRole: statusType === 'error' ? 'alert' : 'status' })} style={[
                    styles.statusText,
                    statusType === 'success' ? styles.statusSuccess : null,
                    statusType === 'error' ? styles.statusError : null,
                  ]}>{status}</Text>
                ) : null}
                <Pressable
                  disabled={isBusy}
                  onPress={goToRegister}
                  {...fl({ accessibilityRole: 'link', accessibilityLabel: 'Create a free account' })}
                  style={({ pressed, hovered }) => [
                    styles.linkPressable,
                    (pressed || hovered) && !isBusy && styles.linkPressableHover,
                  ]}
                >
                  {({ hovered }) => (
                    <Text style={[styles.footerText, isBusy && styles.textDisabled]}>
                      New to BetOptimized?{' '}
                      <Text style={[styles.footerAccent, hovered && !isBusy && styles.footerAccentHover]}>Create a free account</Text>
                    </Text>
                  )}
                </Pressable>
              </View>
            </View>
          </BlurView>
        </Animated.View>
      </Animated.View>
    </View>
  );
}

const SHEETS = lookSheets({
  container: {
    flex: 1,
    backgroundColor: '#000000',
    borderWidth: 2,
    borderColor: '#000',
  },
  mainContent: {
    flex: 1,
    paddingHorizontal: isMobile ? 16 : (Platform.OS === 'web' ? 48 : 20),
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerSection: {
    alignItems: 'center',
    marginBottom: isMobile ? 24 : 32,
    gap: isMobile ? 8 : 10,
  },
  brandTitle: {
    fontFamily: 'ClimateCrisis',
    fontSize: isMobile ? 22 : (Platform.OS === 'web' ? 40 : 28),
    fontWeight: '400',
    lineHeight: isMobile ? 26 : (Platform.OS === 'web' ? 44 : 32),
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.9)',
  },
  brandTagline: {
    fontFamily: FONT,
    fontSize: isMobile ? 10 : 12,
    fontWeight: '500',
    letterSpacing: 2.5,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.45)',
  },
  formContainer: {
    width: '100%',
    maxWidth: 480,
    justifyContent: 'center',
    alignItems: 'center',
  },
  formCard: {
    width: '100%',
    padding: isMobile ? 24 : 40,
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' && { boxShadow: '0 24px 64px rgba(0, 0, 0, 0.45)' }),
  },
  formContent: {
    width: '100%',
    gap: isMobile ? 20 : 24,
    alignItems: 'center',
  },
  titleSection: {
    alignItems: 'center',
    gap: 8,
  },
  welcomeTitle: {
    fontFamily: FONT,
    fontSize: isMobile ? 24 : 30,
    fontWeight: '600',
    lineHeight: isMobile ? 30 : 36,
    textAlign: 'center',
  },
  welcomeSubtitle: {
    fontFamily: FONT,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.55)',
  },
  formSection: {
    width: '100%',
    gap: isMobile ? 14 : 16,
  },
  signInButton: {
    width: '100%',
    marginTop: 4,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(120, 160, 255, 0.35)',
    overflow: 'hidden',
    shadowColor: '#1E50DC',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
    ...webTransition,
  },
  primaryHover: Platform.OS === 'web' ? {
    transform: [{ translateY: -1 }],
    borderColor: 'rgba(150, 185, 255, 0.65)',
    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.28)',
  } : {},
  buttonPressed: {
    transform: [{ scale: 0.985 }],
  },
  buttonGradient: {
    paddingVertical: 14,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
  },
  signInButtonText: {
    fontFamily: FONT,
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    letterSpacing: 0.2,
    color: 'rgba(255, 255, 255, 0.95)',
  },
  altSection: {
    width: '100%',
    gap: isMobile ? 14 : 16,
  },
  dividerSection: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    gap: 12,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
  },
  dividerText: {
    fontFamily: FONT,
    fontSize: 12,
    fontWeight: '400',
    letterSpacing: 0.5,
    color: 'rgba(255, 255, 255, 0.5)',
  },
  googleButton: {
    width: '100%',
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.12)',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 4,
    ...webTransition,
  },
  googleHover: Platform.OS === 'web' ? {
    transform: [{ translateY: -1 }],
    borderColor: 'rgba(255, 255, 255, 0.3)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
  } : {},
  googleButtonGradient: {
    paddingVertical: 13,
    borderRadius: 9,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  googleButtonText: {
    fontFamily: FONT,
    fontSize: 15,
    fontWeight: '500',
    lineHeight: 20,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  footerSection: {
    width: '100%',
    alignItems: 'center',
    gap: 10,
  },
  forgotText: {
    fontFamily: FONT,
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 16,
    color: 'rgba(255, 255, 255, 0.55)',
    ...(Platform.OS === 'web' && { transition: 'color 150ms ease' }),
  },
  linkTextHover: {
    color: 'rgba(255, 255, 255, 0.95)',
  },
  linkPressable: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    alignSelf: 'center',
    borderRadius: 8,
    ...webTransition,
  },
  linkPressableHover: Platform.OS === 'web'
    ? { backgroundColor: 'rgba(255,255,255,0.05)' }
    : {},
  footerText: {
    fontFamily: FONT,
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  footerAccent: {
    color: 'rgba(150, 185, 255, 0.95)',
    fontWeight: '600',
  },
  footerAccentHover: {
    textDecorationLine: 'underline',
  },
  statusText: {
    width: '100%',
    fontFamily: FONT,
    fontSize: 13,
    lineHeight: 18,
    textAlign: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    color: 'rgba(255, 255, 255, 0.85)',
  },
  statusSuccess: {
    color: '#34D399',
    borderColor: 'rgba(52, 211, 153, 0.35)',
    backgroundColor: 'rgba(52, 211, 153, 0.08)',
  },
  statusError: {
    color: '#F87171',
    borderColor: 'rgba(234, 84, 85, 0.35)',
    backgroundColor: 'rgba(234, 84, 85, 0.08)',
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  textDisabled: {
    opacity: 0.5,
  },
}, AUTH_FL_OVERRIDES);
